<template>
  <div class="part2-main-task">
    <div class="task-header">
      <div class="task-avatar"></div>
      <div class="task-content">
        <div>
          <span class="task-content-title"
            >我的積分：{{ lotteryAndTaskState.totalScore }}</span
          >
          <span class="task-content-detail btns" @click="showScoreDetail"
            >查看積分明細 >></span
          >
        </div>
        <div class="task-divider"></div>
      </div>
    </div>

    <!-- 未预约提示 -->
    <div v-if="!isReserved" class="task-unreserved-tips">
      完成新版本預約後即可查看任務列表
    </div>

    <!-- 任务列表 -->
    <div v-else class="task-list" :class="{ 'task-list-loading': isRefreshing }">
      <div
        class="task-list-item"
        v-for="(item, index) in taskList"
        :key="item.key"
        :class="{
          'task-team': item.key === 'task_team_rally',
        }"
      >
        <!-- 组队任务 -->
        <template v-if="item.key === 'task_team_rally' && item.children">
          <div class="task-team-header">
            <div class="task-list-item-content">
              <div class="task-list-item-content-title">{{ item.title }}</div>
              <div class="task-list-item-content-descript">{{ item.descript }}</div>
              <div class="task-list-item-content-tips">{{ getTeamTaskTips }}</div>
            </div>
            <div class="task-list-item-content-right">
              <div
              class="task-btn"
              :class="getTeamBtnClass()"
              @click="handleTaskBtn(item)"
            ></div>
            <div v-if="!hasTeam" class="join-other-btn" @click="handleJoinOtherTeam">
              加入其他隊伍
            </div>
            <div v-if="hasTeam" class="team-detail-btn" @click="handleShowTeamDetail">
              查看隊伍詳情
            </div>
            </div>

          </div>

          <!-- 子任务列表 -->
          <div class="task-team-children">
            <div
              class="task-team-child-item"
              v-for="child in item.children"
              :key="child.level"
            >
              <i class="task-team-item-star"></i>
              <div class="task-team-child-content">
                <div class="task-team-child-title">
                  {{ child.title }}
                  <span class="task-team-child-score">({{ child.score }} 分)</span>
                </div>
                <div class="task-team-child-descript">{{ child.descript }}</div>
              </div>
              <div
                class="task-btn task-team-child-btn"
                :class="getTeamChildBtnClass(child)"
                @click="handleTeamChildBtn(child)"
              ></div>
            </div>
          </div>
        </template>

        <!-- 普通任务 -->
        <template v-else>
          <div class="task-list-item-content">
            <div class="task-list-item-content-title">
              {{ item.title }} <span v-if="item.score" class="task-list-item-content-title-score">({{ item.score }} 分)</span>
            </div>
            <div class="task-list-item-content-descript">
              {{ getCurDescript(item) }}
            </div>
          </div>
          <!-- 任务按钮 -->
          <div
            class="task-btn"
            :class="getTaskBtnClass(item)"
            @click="handleTaskBtn(item)"
          ></div>
        </template>
      </div>
    </div>

    <div class="task-footer">
      <div class="task-rule" @click="showRule">
        活動規則<i class="task-footer-icon"></i>
      </div>
      <div class="task-refresh-btn" @click="handleRefresh"></div>
    </div>
  </div>
</template>

<script setup>
import { inject, reactive, computed, ref } from "vue";
import { pointClickButton } from "@/utils";
import { UgcTab } from "../typings";

const lotteryAndTaskState = inject("lotteryAndTaskState", reactive({}));
const v3PopupState = inject("v3PopupState", reactive({}));
const checkLogin = inject("checkLogin");
const gotoLogin = inject("gotoLogin");
const isLogin = inject("isLogin");
const isReserved = inject("isReserved", ref(false)); // 注入是否已预约状态
const taskListFromParent = inject("taskList", ref([])); // 注入响应式任务列表
const showSharePopup = inject("showSharePopup"); // 注入分享弹窗方法
const activeUgcTab = inject("activeUgcTab", ref(UgcTab.UgcTypeMyROMemory));
const goUgcList = inject("goUgcList");
const goCenter = inject("goCenter");
const initActivity = inject("initActivity");
const refreshTaskStatus = inject("refreshTaskStatus"); // 注入刷新任务状态方法
const showTipMessage = inject("showTipMessage");
const showRulePopup = inject("showRulePopup", () => {}); // 注入规则弹窗方法
const showCreatePopup = inject("showCreatePopup", () => {}); // 注入创建弹窗方法
const showJoinTeamPopup = inject("showJoinTeamPopup", () => {}); // 注入加入队伍弹窗方法
const showTeamListPopup = inject("showTeamListPopup", () => {}); // 注入队伍列表弹窗方法
const claimTaskReward = inject("claimTaskReward"); // 注入领取任务奖励方法
const showCurErrorTips = inject("showCurErrorTips"); // 注入错误提示方法
const hasTeam = inject("hasTeam", computed(() => false)); // 注入队伍状态
const myCode = inject("myCode", ref('')); // 注入队伍邀请码
const membersCount = inject("membersCount", ref(0)); // 注入队伍人数

const canClick = ref(true);
const isRefreshing = ref(false); // 任务刷新中状态

const taskList = computed(() => {
  // 直接使用从父组件注入的响应式任务列表
  return taskListFromParent.value;
});

// 计算组队任务的 tips 文案
const getTeamTaskTips = computed(() => {
  // 如果有队伍（myCode 不为空），显示队伍人数
  if (myCode.value) {
    return `我的隊伍人數：${membersCount.value}人`;
  }
  // 没有队伍，显示默认提示
  return '※創建/加入隊伍後不可退出';
});

const getCurDescript = (item) => {
  if (item.progress !== void 0) {
    return item.descript.replace("{n}", item.progress || 0);
  }
  return item.descript;
};

// 获取普通任务按钮的类名
const getTaskBtnClass = (item) => {
  // 活动已结束
  if (lotteryAndTaskState.isEnd) {
    return 'task-btn-end';
  }

  // 使用接口返回的 status 字段（数字类型）
  // status: 0-未完成, 1-已完成可领取, 2-已领取
  if (item.status === 2 || item.disabled) {
    return 'task-btn-finished';
  }

  if (item.status === 1) {
    return 'task-btn-get';
  }

  // status === 0（未完成）
  // 如果任务配置了 hideGoButton（如每日登录游戏），显示"未完成"状态
  if (item.hideGoButton) {
    return 'task-btn-unfinished';
  }

  // 默认状态：去完成
  return 'task-btn-go';
};

// 获取组队任务按钮的类名
const getTeamBtnClass = () => {
  if (hasTeam.value) {
    return 'task-btn-team-invite';
  }

  return 'task-btn-team-create';
};

// 获取组队子任务按钮的类名
const getTeamChildBtnClass = (child) => {
  // 活动已结束
  if (lotteryAndTaskState.isEnd) {
    return 'task-btn-end';
  }

  // 使用接口返回的 status 字段（数字类型）
  // status: 0-未完成, 1-已完成, 2-已领取
  if (child.status === 2 || child.disabled) {
    return 'task-btn-finished';
  }

  if (child.status === 1 || child.completed) {
    return 'task-btn-get';
  }

  // 未完成状态
  return 'task-btn-unfinished';
};

const showScoreDetail = () => {
  if (!checkLogin()) {
    return;
  }
  pointClickButton("积分明细", "任务页面");
  v3PopupState.scoreDetailPopupState = true;
};

const handleTaskBtn = async (item) => {
  if (item.login && !isLogin.value) {
    gotoLogin();
    return;
  }
  if (!checkLogin()) {
    return;
  }

  // 活动已结束，提示用户
  if (lotteryAndTaskState.isEnd) {
    showTipMessage('活動已結束');
    return;
  }

  // 已领取或禁用状态不允许点击
  if (item.status === 2 || item.disabled) {
    return;
  }

  // hideGoButton 为 true 且 status=0（未完成）时不可点击
  if (item.hideGoButton && item.status === 0) {
    return;
  }

  if (!canClick.value) {
    return;
  }

  // 如果是组队任务且是创建队伍状态，显示创建弹窗
  if (item.key === 'task_team_rally') {
    const btnClass = getTeamBtnClass();
    if (btnClass === 'task-btn-team-create') {
      showCreatePopup();
      return;
    } else if (btnClass === 'task-btn-team-invite') {
      // 已有队伍，点击邀请按钮触发分享
      handleTeamInvite();
      return;
    }
  }

  // 如果任务状态是 1（已完成可领取），调用领取奖励接口
  if (item.status === 1 && item.taskKey) {
    canClick.value = false;
    const result = await claimTaskReward(item.taskKey, initActivity);
    if (result.success) {
      showTipMessage(result.msg);
    } else {
      showCurErrorTips(result.code);
    }
    canClick.value = true;
    return;
  }

  // 如果是每日分享任务，触发分享功能
  if (item.key === 'task_daily_share') {
    handleDailyShare();
    return;
  }

  // 如果有跳转链接，直接跳转
  canClick.value = false;
  if (item.redirectUrl) {
    let curLink =
      item.key == "task_reserve_external"
        ? window.globalGetGotoLink({ link: item.redirectUrl })
        : item.redirectUrl;
    window.open(curLink, "_blank");
  }
  canClick.value = true;
};

const showRule = () => {
  pointClickButton("规则说明", "任务页面");
  // 使用 inject 的 showRulePopup 方法来显示规则弹窗
  showRulePopup();
};

// 刷新任务列表
const handleRefresh = async () => {
  if (!checkLogin()) {
    gotoLogin();
    return;
  }

  if (isRefreshing.value) {
    return; // 防止重复点击
  }

  pointClickButton("刷新任务", "任务页面");

  isRefreshing.value = true;
  try {
    await refreshTaskStatus?.();
  } catch (error) {
    console.error('刷新任务失败:', error);
  } finally {
    isRefreshing.value = false;
  }
};

// 处理组队子任务按钮点击
const handleTeamChildBtn = async (child) => {
  if (!checkLogin()) {
    return;
  }

  // 活动已结束、已领取或未完成状态不允许点击
  if (lotteryAndTaskState.isEnd || child.status === 2 || child.disabled || child.status === 0) {
    return;
  }

  // 如果状态是 1（已完成可领取），调用领取奖励接口
  if (child.status === 1 && child.taskKey) {
    if (!canClick.value) {
      return;
    }

    canClick.value = false;
    const result = await claimTaskReward(child.taskKey, initActivity);
    if (result.success) {
      showTipMessage(result.msg);
    } else {
      showCurErrorTips(result.code);
    }
    canClick.value = true;
  }
};

// 点击加入其他队伍
const handleJoinOtherTeam = () => {
  if (!checkLogin()) {
    gotoLogin();
    return;
  }

  // 活动已结束，提示用户
  if (lotteryAndTaskState.isEnd) {
    showTipMessage('活動已結束');
    return;
  }

  pointClickButton("加入其他队伍", "任务页面");
  showJoinTeamPopup();
};

// 点击查看队伍详情
const handleShowTeamDetail = () => {
  if (!checkLogin()) {
    gotoLogin();
    return;
  }
  pointClickButton("查看队伍详情", "任务页面");
  showTeamListPopup();
};

// 处理队伍邀请分享
const handleTeamInvite = async () => {
  if (!checkLogin()) {
    gotoLogin();
    return;
  }

  pointClickButton("邀请队员", "任务页面");

  // 确保有邀请码（teamCode）
  if (!myCode.value) {
    console.log('📌 邀请码为空，刷新活动数据以获取 teamCode...');
    // 刷新活动数据以获取 teamCode（myCode）
    await initActivity?.();

    // 如果刷新后还是没有邀请码，提示用户
    if (!myCode.value) {
      showTipMessage?.('獲取邀請碼失敗，請稍後重試');
      console.error('❌ 刷新后仍无法获取 teamCode');
      return;
    }
  }

  console.log('✅ 邀请码已准备:', myCode.value);

  // 🆕 调用 getTeamMembers 接口获取队伍信息并打点
  try {
    const { getJSON } = await import('@/api/request');
    const { getActivityApi } = await import('@/utils');
    const { point } = await import('@/utils');

    const url = getActivityApi('global', 'row_reservation_01', 'getTeamMembers');
    const res = await getJSON(url, {
      params: JSON.stringify({})
    });

    if (res.code === 1 && res.result === 1 && res.data?.members) {
      const members = res.data.members;

      // 找到队长信息（role === 'leader'）
      const leader = members.find((member) => member.role === 'leader');

      // 打点上报：邀请好友组队
      point({
        eventName: 'invite_friends',
        eventType: '邀请好友组队',
        eventValue: JSON.stringify({
          teamCode: myCode.value,                       // 组队码信息
          creatorAccount: leader?.loginAccount || '',  // 创建者账号
          membersCount: members.length,                 // 队伍成员数量
          createTime: leader?.joinTime || '',           // 队伍创建时间
        })
      });
    } else {
      console.warn('⚠️ 获取队伍成员信息失败，跳过打点:', res.msg);
    }
  } catch (error) {
    console.error('❌ 获取队伍成员信息失败:', error);
  }

  // 打开组队邀请分享弹窗
  showSharePopup('team_invite');
};

// 处理每日分享
const handleDailyShare = () => {
  if (!checkLogin()) {
    gotoLogin();
    return;
  }

  pointClickButton("每日分享", "任务页面");
  // 打开普通分享弹窗
  showSharePopup('daily');
};
</script>

<style scoped lang="less"></style>
